import React, { FC, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import WeeklyBusinessEfficiency from '../../../../components/report/detail/weekly/WeeklyBusinessEfficiency'
import WeeklyConcentration from '../../../../components/report/detail/weekly/WeeklyConcentration'
import WeeklyHeader from '../../../../components/report/detail/weekly/WeeklyHeader'
import WeeklyMember from '../../../../components/report/detail/weekly/WeeklyMember'
import WeeklyWorkload from '../../../../components/report/detail/weekly/WeeklyWorkload'
import HeatmapModal from '../../../../components/report/detail/HeatmapModal'
import { getGetPrams, setPath, setUrlParam } from '../../../../../utils/url'
import {
  endOfLastWeek,
  endOfNextWeek,
  endOfWeek,
  format,
  nextMonth,
  nowDate,
  prevMonth,
  secToHourMinute,
  startOfLastWeek,
  startOfNextWeek,
  startOfWeek,
  weekList,
} from '../../../../../utils/date'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../../states/containers/store'
import WeeklyCategory from '../../../../components/report/detail/weekly/WeeklyCategory'
import WeeklyApplication from '../../../../components/report/detail/weekly/WeeklyApplication'
import { fetchCompare, fetchWeeklyMemberReport, fetchWeeklyTeamReport } from '../../../../../states/containers/report/reportSlice'
import { ResponseWeeklyMemberReport, ResponseWeeklyTeamReport } from '../../../../../states/types/report'
import SearchModal from '../../../../components/report/detail/SearchModal'
import { fetchKeyword, fetchSearchRequest } from '../../../../../states/containers/search/listSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { URL } from '../../../../constants/url'
import { useTranslation } from 'react-i18next'
import Loading from '../../../../components/common/Loading'
import Wordcloud from '../../../../components/report/detail/Wordcloud'
import { SearchRequestParams } from '../../../../../states/types/search'
import PdfModule from '../../../../components/common/PdfModule'

interface HeatmapModal {
  display: boolean
  startDate: string
  endDate: string
  type: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  displaySearchLink: boolean
  name: string
}

interface SearchModal {
  startDate: string
  endDate: string
  teamIds?: number[]
  teamNames?: string[]
  memberIds?: number[]
  memberNames?: string[]
  keywords?: string[]
  serviceNames?: string[]
  categoryIds?: number[]
  categoryNames?: string[]
  efficiencyLevel?: string[]
  stressLevel?: string[]
  focusLevel?: string[]
  lowerLimitTime?: number
  sortKey?: string,
  sortType?: string,
}

type LangValue = 'ja' | 'en'

interface GetParams {
  date: string
}

const Index: FC = () => {
  const { target } = useParams<{ target: string | undefined }>()
  const { id } = useParams<{ id: string | undefined }>()
  const { t } = useTranslation()
  const location = useLocation()
  const dispatch = useDispatch()
  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()

  const LOWER_LIMIT_TIME_DEFAULT = 300

  const {
    login,
    report,
    commonPart,
    profile,
  } = useSelector((state: RootState) => state)

  const heatmapModalInit = {
    display: false,
    startDate: "",
    endDate: "",
    type: "",
    target: "",
    id: 0,
    title: "",
    legend: "",
    startColor: "",
    endColor: "",
    minimum: 0,
    displaySearchLink: false,
    name: "",
  }

  const searchModalInit = {
    startDate: "",
    endDate: "",
    lowerLimitTime: LOWER_LIMIT_TIME_DEFAULT,
  }

  const [heatmapModal, setHeatmapModal] = useState<HeatmapModal>(heatmapModalInit)
  const [searchModal, setSearchModal] = useState<SearchModal>(searchModalInit)

  const [startDate, setStartDate] = useState<string>()

  const [data, setData] = useState<ResponseWeeklyTeamReport | ResponseWeeklyMemberReport>()

  const [isSearchModalDisplay, setIsSearchModalDisplay] = useState<boolean>(false)

  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)

  const [tempStartDate, setTempStartDate] = useState<string>()

  const [isClickable, setIsClickable] = useState(true)

  const [isChange, setIsChange] = useState(false)

  const [targetId, setTargetId] = useState<number>(0)

  const [isDisplaySticky, setIsDisplaySticky] = useState(false)

  const [referrerState, setReferrerState] = useState({
    title: "",
    url: "",
  })

  const pdfTitle = useMemo(() => {
    let nameText = ""
    if (data && target === "team" && "team" in data) {
      nameText = `${data.team?.name}チーム`
    } else if (data && target === "member" && "member" in data) {
      nameText = `${data.member?.name}さん`
    }
    // eslint-disable-next-line max-len
    return `Qasee週間業務レポート ${nameText} ${format(startOfWeek(startDate || nowDate()), "YYYY年MM月DD日")} ~ ${format(endOfWeek(startDate || nowDate()), "YYYY年MM月DD日")}`
  }, [data])

  const scrollTop = (): number => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)

  const displayHeatmapModal = (display: boolean) => {
    setHeatmapModal({
      ...heatmapModal,
      display: display,
    })
  }

  const heatmapModalDetail = (heatmapDetail: HeatmapModal) => {
    setHeatmapModal({
      ...heatmapModal,
      display: heatmapDetail.display,
      startDate: heatmapDetail.startDate,
      endDate: heatmapDetail.endDate,
      type: heatmapDetail.type,
      target: heatmapDetail.target,
      id: heatmapDetail.id,
      title: heatmapDetail.title,
      legend: heatmapDetail.legend,
      startColor: heatmapDetail.startColor,
      endColor: heatmapDetail.endColor,
      minimum: heatmapDetail.minimum,
      displaySearchLink: heatmapDetail.displaySearchLink,
      name: heatmapDetail.name,
    })
    if (data && "member" in data && data.member) {
      dispatch(fetchCompare(
        {
          start_date: data?.date?.from || startOfLastWeek(nowDate()),
          end_date: data?.date?.to || endOfLastWeek(nowDate()),
          target: "member",
          team_id: data?.member?.team_id,
          member_ids: [data.member?.id],
          indicator: heatmapDetail.title,
          name: heatmapDetail.name,
        },
      ))
    } else if (data && "team" in data && data.team) {
      dispatch(fetchCompare(
        {
          start_date: data?.date?.from || startOfLastWeek(nowDate()),
          end_date: data?.date?.to || endOfLastWeek(nowDate()),
          target: "team",
          team_id: data?.team.id,
          member_ids: data.team.members.map((member) => member.id),
          indicator: heatmapDetail.title,
          name: heatmapDetail.name,
        },
      ))
    }
  }

  const searchModalDetail = (searchDetail: SearchModal) => {
    setIsSearchModalDisplay(true)
    setSearchModal({
      startDate: searchDetail.startDate,
      endDate: searchDetail.endDate,
      teamIds: searchDetail.teamIds,
      teamNames: searchDetail.teamNames,
      memberIds: searchDetail.memberIds,
      memberNames: searchDetail.memberNames,
      keywords: searchDetail.keywords,
      serviceNames: searchDetail.serviceNames,
      categoryIds: searchDetail.categoryIds,
      categoryNames: searchDetail.categoryNames,
      efficiencyLevel: searchDetail.efficiencyLevel,
      stressLevel: searchDetail.stressLevel,
      focusLevel: searchDetail.focusLevel,
      lowerLimitTime: searchDetail.lowerLimitTime || LOWER_LIMIT_TIME_DEFAULT,
      sortKey: searchDetail.sortKey,
      sortType: searchDetail.sortType,
    })
    const requestParams: SearchRequestParams = {
      start_date: searchDetail.startDate,
      end_date: searchDetail.endDate,
      team_ids: searchDetail.teamIds || [],
      member_ids: searchDetail.memberIds || [],
      keywords: searchDetail.keywords || [],
      service_names: searchDetail.serviceNames || [],
      category_ids: searchDetail.categoryIds || [],
      efficiency_level: searchDetail.efficiencyLevel || [],
      stress_level: searchDetail.stressLevel || [],
      focus_level: searchDetail.focusLevel || [],
      lower_limit_time: searchDetail.lowerLimitTime || LOWER_LIMIT_TIME_DEFAULT,
    }
    if (searchDetail.sortKey) {
      requestParams.sort_key = searchDetail.sortKey
    }
    if (searchDetail.sortType) {
      requestParams.sort_type = searchDetail.sortType
    }
    dispatch(fetchSearchRequest(
      requestParams,
      () => {
        //
      },
    ))
  }

  const handleChangeWeek = (value: string) => {
    setIsCalendarDisplay(false)
    if (!target || !id) return
    setStartDate(format(value))
    setIsClickable(false)
    history.push(`${setPath(setUrlParam(
      URL.REPORT_DETAIL_WEEKLY.path,
      [
        { key: 'target', value: target },
        { key: 'id', value: id },
      ],
    ), language)}?date=${value}`)
  }

  const buildName = () => {
    if (data && target === "team" && "team" in data) {
      return (
        <>
          {data.team?.name}
          チーム
        </>
      )
    }
    if (data && target === "member" && "member" in data) {
      return (
        <>
          {data.member?.name}
          さん
        </>
      )
    }
    return ""
  }

  const onScroll = (): void => {
    if (scrollTop() <= 800) {
      setIsDisplaySticky(false)
    } else {
      setIsDisplaySticky(true)
    }
  }

  useEffect(() => {
    if (report.isLoading) return
    setIsClickable(true)
    if (target === "team" && report.weeklyTeamReport) {
      setData(report.weeklyTeamReport)
      dispatch(fetchKeyword({
        start_date: format(report.weeklyTeamReport.date?.from, "YYYY-MM-DD") || format(startOfWeek(nowDate()), "YYYY-MM-DD"),
        end_date: format(report.weeklyTeamReport.date?.to, "YYYY-MM-DD") || format(endOfWeek(nowDate()), "YYYY-MM-DD"),
        member_ids: report.weeklyTeamReport.team?.members.map((member) => member.id) || [],
      }))
      return
    }
    if (target === "member" && report.weeklyMemberReport) {
      setData(report.weeklyMemberReport)
      dispatch(fetchKeyword({
        start_date: format(report.weeklyMemberReport.date?.from, "YYYY-MM-DD") || format(startOfWeek(nowDate()), "YYYY-MM-DD"),
        end_date: format(report.weeklyMemberReport.date?.to, "YYYY-MM-DD") || format(endOfWeek(nowDate()), "YYYY-MM-DD"),
        member_ids: report.weeklyMemberReport.member?.id ? [report.weeklyMemberReport.member?.id] : [],
      }))
    }
  }, [report.isLoading])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (data?.summary?.total_evaluation === "Excellent" && !report.isLoading) {
      document.getElementsByClassName('bg')[0]?.classList.add('excellent')
    } else {
      document.getElementsByClassName('bg')[0]?.classList.remove('excellent')
    }
  }, [data, report.isLoading])

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (startDate === getParams.date && targetId === Number(id)) return
    if (!getParams.date) {
      setStartDate(startOfLastWeek(nowDate()))
      setTempStartDate(nowDate("YYYY-MM-01"))
    } else {
      setStartDate(format(getParams.date))
      setTempStartDate(format(getParams.date, "YYYY-MM-01"))
    }
    setTargetId(Number(id))
    setIsChange(true)
  }, [location])

  useEffect(() => {
    if (!login.accessToken || !startDate || !targetId || !isChange) return
    if (target === "team") {
      dispatch(fetchWeeklyTeamReport(targetId, { start_date: format(startDate, "YYYY-MM-DD") }))
    } else {
      dispatch(fetchWeeklyMemberReport(targetId, { start_date: format(startDate, "YYYY-MM-DD") }))
    }
    setIsChange(false)
  }, [isChange])

  useEffect(() => {
    if (!login.accessToken) return
    document.addEventListener("scroll", onScroll)
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
  }, [login.accessToken])

  useEffect(() => {
    const state = location.state as { referrer: string }
    if (!state || !state.referrer || !state.referrer.match("/report")) {
      setReferrerState({
        title: "",
        url: "",
      })
      return
    }
    const referrerPath = state.referrer.split(`${language}/`)[1]
    const referrerArray = referrerPath.split("?")[0].split("/")
    let range = ""
    if (referrerArray[1] === "monthly") {
      range = "月間改善レポート"
    } else {
      range = "週間業務レポート"
    }
    let name = ""
    if (referrerArray[2] === "team") {
      name = `${commonPart.teams?.find((team) => team.id === Number(referrerArray[3]))?.name || ""}チーム`
    } else {
      name = `${commonPart.members?.find((member) => member.id === Number(referrerArray[3]))?.name || ""}さん`
    }
    setReferrerState({
      title: `${range} ${name}`,
      url: `${setPath(URL.ROOT.path, language)}${referrerPath}`,
    })
  }, [location])

  return (
    <section className="report_page">
      <Helmet>
        <title>Qasee - AI業務分析 - 週間業務レポート</title>
      </Helmet>
      <section className="breadcrumb">
        {
          data && (
            <>
              <Link
                to={setPath(URL.REPORT.path, language)}
              >
                レポート一覧
              </Link>
              {
                referrerState.title && (
                  <>
                    &nbsp;&gt;&nbsp;
                    <Link
                      to={referrerState.url}
                    >
                      {referrerState.title}
                    </Link>
                  </>
                )
              }
              &nbsp;&gt;&nbsp;週間業務レポート&nbsp;
              {buildName()}
            </>
          )
        }
      </section>
      {
        data && (
          <section className={`sticky_header ${isDisplaySticky ? 'active' : ''}`}>
            <div>
              <div className="bread_area">
                <Link
                  to={setPath(URL.REPORT.path, language)}
                >
                  レポート一覧
                </Link>
                {
                  referrerState.title && (
                    <>
                      &nbsp;&gt;&nbsp;
                      <Link
                        to={referrerState.url}
                      >
                        {referrerState.title}
                      </Link>
                    </>
                  )
                }
                &nbsp;&gt;&nbsp;週間業務レポート&nbsp;
                {buildName()}
              </div>
              <div className="title_area">
                {format(startOfWeek(startDate || nowDate()), "YYYY年MM月DD日")}
                &nbsp;~&nbsp;
                {format(endOfWeek(startDate || nowDate()), "YYYY年MM月DD日")}
                &nbsp;週間業務レポート&nbsp;
                {buildName()}
              </div>
            </div>
            {
              data && "member" in data && data.member && (
                <div className="items">
                  {
                    data.member?.applied_work_styles?.length ? (
                      <>
                        <div className="item">
                          <img
                            alt=""
                            src={`/static/images/work_style/${
                              (data.member.applied_work_styles?.length && data.member.applied_work_styles[0].id) || 0
                            }.png`}
                            className="icon"
                          />
                          <div className="item_title">
                            <div>働き方</div>
                            {
                              profile.work_style_list.find(
                                (work_style) => work_style.id === (data.member?.applied_work_styles[0].id),
                              )?.description
                            }
                          </div>
                        </div>
                      </>
                    ) : (
                      <></>
                    )
                  }
                  {
                    data.member?.applied_occupation?.id ? (
                      <div className="item">
                        <img
                          alt=""
                          src={`/static/images/occupation/${data.member.applied_occupation?.id || 0}.png`}
                          className="icon"
                        />
                        <div className="item_title">
                          <div>職種</div>
                          {
                            profile.occupationList.find(
                              (occupation) => occupation.id === data.member?.applied_occupation?.id,
                            )?.description
                          }
                        </div>
                      </div>
                    ) : (
                      <></>
                    )
                  }
                  {
                    data.member?.applied_work_styles?.length || data.member?.applied_occupation?.id ? (
                      <div className="item">
                        <div className="text_area">
                          <div>
                            {secToHourMinute(data.member.base_work_time || 0)}
                          </div>
                        </div>
                        <div className="item_title">
                          <div>勤務時間</div>
                          {secToHourMinute(data.member.base_work_time || 0)}
                        </div>
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
              )
            }
          </section>
        )
      }
      <section className="page_header">
        <div className="report_title">
          週間業務レポート
        </div>
        <div className="tools">
          <div className="calendar_text">
            <button
              type="button"
              className="display button"
              onClick={() => setIsCalendarDisplay(true)}
              disabled={!isClickable}
            >
              {format(startOfWeek(startDate || nowDate()), "YYYY-MM-DD")}
              ~
              {format(endOfWeek(startDate || nowDate()), "YYYY-MM-DD")}
              <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isCalendarDisplay && 'open'}`} />
            </button>
            <button
              type="button"
              className="prev button"
              onClick={() => handleChangeWeek(format(startOfLastWeek(startDate || nowDate()), "YYYY-MM-DD"))}
              disabled={
                !commonPart.company?.created_at
                || Number(format(startDate || nowDate(), "YYYYMMDD"))
                  < Number(format(commonPart.company?.created_at, "YYYYMMDD"))
                || !isClickable
              }
            >
              <FontAwesomeIcon icon={['fas', 'angle-left']} />
            </button>
            <button
              type="button"
              className="next button"
              onClick={() => handleChangeWeek(format(startOfNextWeek(startDate || nowDate()), "YYYY-MM-DD"))}
              disabled={
                !commonPart.company?.created_at
                || Number(format(endOfNextWeek(startDate || nowDate()), "YYYYMMDD"))
                  >= Number(format(endOfWeek(nowDate()), "YYYYMMDD"))
                || !isClickable
              }
            >
              <FontAwesomeIcon icon={['fas', 'angle-right']} />
            </button>
          </div>
          <div className={`calendar_box ${isCalendarDisplay || 'none'}`}>
            <div className="range">
              <div className="text">
                {t(`range.calendar.header_text.weekly`)}
              </div>
            </div>
            <div className="monthly">
              <div className="select_month">
                <div>
                  <button
                    type="button"
                    onClick={() => setTempStartDate(prevMonth(tempStartDate || nowDate(), "YYYY-MM-01"))}
                    disabled={Number(format(commonPart.company?.created_at, 'YYYYMM')) >= Number(format((tempStartDate || nowDate()), "YYYYMM"))}
                  >
                    <FontAwesomeIcon icon={['fas', 'angle-left']} />
                  </button>
                </div>
                <div>
                  {format(tempStartDate || nowDate(), "YYYY年MM月")}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setTempStartDate(nextMonth(tempStartDate || nowDate(), "YYYY-MM-01"))}
                    disabled={Number(nowDate("YYYYMM")) <= Number(format((tempStartDate || nowDate()), "YYYYMM"))}
                  >
                    <FontAwesomeIcon icon={['fas', 'angle-right']} />
                  </button>
                </div>
              </div>
              <div
                style={{
                  textAlign: "center",
                }}
              >
                {
                  weekList(format(tempStartDate || nowDate()), "YYYY-MM-DD").map((week) => (
                    <React.Fragment key={`week_list_${week}`}>
                      <button
                        type="button"
                        onClick={() => handleChangeWeek(format(startOfWeek(week), "YYYY-MM-DD"))}
                        style={{
                          border: "solid 1px #CCC",
                          borderRadius: "4px",
                          marginBottom: "8px",
                        }}
                        disabled={
                          !commonPart.company?.created_at
                          || Number(format(endOfWeek(week || nowDate()), "YYYYMMDD"))
                            < Number(format(commonPart.company?.created_at, "YYYYMMDD"))
                          || Number(format(endOfWeek(week || nowDate()), "YYYYMMDD"))
                            >= Number(format(endOfWeek(nowDate()), "YYYYMMDD"))
                          || !isClickable
                        }
                      >
                        {format(startOfWeek(week), "YYYY-MM-DD")}
                        ~
                        {format(endOfWeek(week), "YYYY-MM-DD")}
                        業務レポート
                      </button>
                    </React.Fragment>
                  ))
                }
              </div>
            </div>
          </div>
          <div className={`close_screen gray ${isCalendarDisplay || 'none'}`} role="presentation" onClick={() => setIsCalendarDisplay(false)} />
        </div>
      </section>
      <section className="page_header">
        <div />
        <div className="tools">
          <PdfModule
            title={pdfTitle}
            target="pdf_area"
          />
        </div>
      </section>
      <section id="pdf_area">
        {
          report.isLoading ? (
            <Loading />
          ) : (
            <>
              {
                target && data?.is_current_data_exist ? (
                  <>
                    <WeeklyHeader
                      target={target}
                      heatmapModalOpenCallback={
                        (heatmap) => heatmapModalDetail({
                          display: true,
                          startDate: data?.date?.from || startOfWeek(nowDate()),
                          endDate: data?.date?.to || endOfWeek(nowDate()),
                          type: heatmap.type,
                          target: heatmap.target,
                          id: heatmap.id,
                          title: heatmap.title,
                          legend: heatmap.legend,
                          startColor: heatmap.startColor,
                          endColor: heatmap.endColor,
                          minimum: heatmap.minimum,
                          displaySearchLink: heatmap.displaySearchLink,
                          name: heatmap.name,
                        })
                      }
                    />
                    <WeeklyBusinessEfficiency
                      target={target}
                      heatmapModalOpenCallback={
                        (heatmap) => heatmapModalDetail({
                          display: true,
                          type: heatmap.type,
                          startDate: data?.date?.from || startOfWeek(nowDate()),
                          endDate: data?.date?.to || endOfWeek(nowDate()),
                          target: heatmap.target,
                          id: heatmap.id,
                          title: heatmap.title,
                          legend: heatmap.legend,
                          startColor: heatmap.startColor,
                          endColor: heatmap.endColor,
                          minimum: heatmap.minimum,
                          displaySearchLink: heatmap.displaySearchLink,
                          name: heatmap.name,
                        })
                      }
                    />
                    <WeeklyWorkload
                      target={target}
                      heatmapModalOpenCallback={
                        (heatmap) => heatmapModalDetail({
                          display: true,
                          type: heatmap.type,
                          startDate: data?.date?.from || startOfWeek(nowDate()),
                          endDate: data?.date?.to || endOfWeek(nowDate()),
                          target: heatmap.target,
                          id: heatmap.id,
                          title: heatmap.title,
                          legend: heatmap.legend,
                          startColor: heatmap.startColor,
                          endColor: heatmap.endColor,
                          minimum: heatmap.minimum,
                          displaySearchLink: heatmap.displaySearchLink,
                          name: heatmap.name,
                        })
                      }
                    />
                    <WeeklyConcentration
                      target={target}
                      heatmapModalOpenCallback={
                        (heatmap) => heatmapModalDetail({
                          display: true,
                          type: heatmap.type,
                          startDate: data?.date?.from || startOfWeek(nowDate()),
                          endDate: data?.date?.to || endOfWeek(nowDate()),
                          target: heatmap.target,
                          id: heatmap.id,
                          title: heatmap.title,
                          legend: heatmap.legend,
                          startColor: heatmap.startColor,
                          endColor: heatmap.endColor,
                          minimum: heatmap.minimum,
                          displaySearchLink: heatmap.displaySearchLink,
                          name: heatmap.name,
                        })
                      }
                    />
                    {
                      target === "team" && (
                        <WeeklyMember />
                      )
                    }
                    <WeeklyCategory
                      target={target}
                      searchModalOpenCallback={
                        (search) => searchModalDetail({
                          startDate: search.startDate,
                          endDate: search.endDate,
                          teamIds: search.teamIds,
                          teamNames: search.teamNames,
                          memberIds: search.memberIds,
                          memberNames: search.memberNames,
                          keywords: search.keywords,
                          serviceNames: search.serviceNames,
                          categoryIds: search.categoryIds,
                          categoryNames: search.categoryNames,
                          efficiencyLevel: search.efficiencyLevel,
                          stressLevel: search.stressLevel,
                          focusLevel: search.focusLevel,
                          lowerLimitTime: searchModal.lowerLimitTime || search.lowerLimitTime,
                          sortKey: search.sortKey,
                          sortType: search.sortType,
                        })
                      }
                    />
                    <WeeklyApplication
                      target={target}
                      searchModalOpenCallback={
                        (search) => searchModalDetail({
                          startDate: search.startDate,
                          endDate: search.endDate,
                          teamIds: search.teamIds,
                          teamNames: search.teamNames,
                          memberIds: search.memberIds,
                          memberNames: search.memberNames,
                          keywords: search.keywords,
                          serviceNames: search.serviceNames,
                          categoryIds: search.categoryIds,
                          categoryNames: search.categoryNames,
                          efficiencyLevel: search.efficiencyLevel,
                          stressLevel: search.stressLevel,
                          focusLevel: search.focusLevel,
                          lowerLimitTime: searchModal.lowerLimitTime || search.lowerLimitTime,
                          sortKey: search.sortKey,
                          sortType: search.sortType,
                        })
                      }
                    />
                    <Wordcloud
                      startDate={data?.date?.from || startOfWeek(nowDate())}
                      endDate={data?.date?.to || endOfWeek(nowDate())}
                    />
                    <HeatmapModal
                      type="weekly"
                      target={target}
                      heatmap={heatmapModal}
                      closeCallback={() => displayHeatmapModal(false)}
                      searchModalOpenCallback={
                        (search) => searchModalDetail({
                          startDate: search.startDate,
                          endDate: search.endDate,
                          teamIds: search.teamIds,
                          teamNames: search.teamNames,
                          memberIds: search.memberIds,
                          memberNames: search.memberNames,
                          keywords: search.keywords,
                          serviceNames: search.serviceNames,
                          categoryIds: search.categoryIds,
                          categoryNames: search.categoryNames,
                          efficiencyLevel: search.efficiencyLevel,
                          stressLevel: search.stressLevel,
                          focusLevel: search.focusLevel,
                          sortKey: search.sortKey,
                          sortType: search.sortType,
                        })
                      }
                    />
                    <SearchModal
                      type="weekly"
                      target={target}
                      searchValue={searchModal}
                      isDisplay={isSearchModalDisplay}
                      closeCallback={() => setIsSearchModalDisplay(false)}
                    />
                  </>
                ) : (
                  <>
                    レポートがありません
                  </>
                )
              }
            </>
          )
        }
      </section>
    </section>
  )
}

export default Index
